<script setup lang="ts">
import BillingCouponLinkModal from '@/components/BillingCouponLinkModal/BillingCouponLinkModal.vue';
import { identifyMarketing } from '@/lib/marketing';
import { useAccountsStore } from '@/stores/accounts';
import { useUserStore } from '@/stores/user';
import { isDesktopApp, needsTitleBar } from '@/use/desktop';
import { useEnvBadge } from '@/use/envBadge';
import { Confirm, Loader, Prompt, Toaster, useToastStore } from '@dabble/quillui';
import { storeToRefs } from 'pinia';
import posthog from 'posthog-js';
import { onBeforeMount, watch, watchEffect } from 'vue';

const toastStore = useToastStore();
toastStore.updateConfig({ position: 'top-center' });

const { userDataReady } = storeToRefs(useAccountsStore());
const userStore = useUserStore();
const { badgeText, badgeTitle } = useEnvBadge();

let identifiedUid = '';
watch(
  () => ({ uid: userStore.user.uid, email: userStore.user.email, name: userStore.user.name }),
  ({ uid, email, name }) => {
    if (uid && email && uid !== identifiedUid) {
      posthog.identify(uid, { email, name });
      // Attach the user to the ad pixels for conversion attribution. No-op unless
      // marketing initialized — which is gated to the production host and skips
      // impersonated tabs (see initMarketing).
      identifyMarketing({ uid, email });
      identifiedUid = uid;
    }
  },
  { immediate: true },
);

onBeforeMount(() => {
  document.documentElement.dataset.theme = 'light';
});

// The desktop shell is frameless on macOS, so accounts supplies the title bar the window
// doesn't have: `in-desktop-app` holds every page's chrome below the band, and the strip
// below is the band itself (see @/styles/desktop-app.scss). 40px is the height 2.5's drag
// header used, which the traffic lights sit inside.
document.body.classList.toggle('in-desktop-app', isDesktopApp);
watchEffect(() => {
  const root = document.documentElement.style;
  if (needsTitleBar.value) root.setProperty('--titlebar-height', '40px');
  else root.removeProperty('--titlebar-height');
});
</script>

<template>
  <div class="app">
    <div v-if="needsTitleBar" class="app__titlebar" data-testid="app-titlebar" />
    <div v-if="badgeText" class="app__env-badge" :title="badgeTitle">{{ badgeText }}</div>
    <div v-if="!userDataReady" class="app__loader" data-testid="app-loader">
      <Loader />
    </div>
    <template v-else>
      <router-view v-slot="{ Component }">
        <transition name="fade" mode="out-in">
          <component :is="Component" />
        </transition>
      </router-view>
      <!-- Promo links (`/billing?coupon=CODE`) open over the billing page they name. -->
      <BillingCouponLinkModal />
      <Toaster />
      <Prompt />
      <Confirm />
    </template>
  </div>
</template>

<style lang="scss" scoped>
.app {
  min-height: 100vh;
  min-height: 100dvh;
  background-color: theme('color-bg-app-overlay');
  color: theme('color-text-primary');

  // The window's title bar, in the desktop app only: the one place the OS lets you drag the
  // frameless macOS window from. A wash of light — the glass surfaces' subtle highlight —
  // so the strip shows itself as something to grab without becoming chrome. It sits above
  // the page but below the overlays, which opt out of the drag region so their clicks land.
  &__titlebar {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    height: var(--titlebar-height, 0px);
    z-index: layer('menu');
    background: theme('color-glass-highlight-subtle');
    -webkit-app-region: drag;
  }

  &__env-badge {
    cursor: help;
    position: fixed;
    bottom: var(--space-tiny);
    left: var(--space-tiny);
    background: var(--color-brand-red);
    color: var(--color-text-primary-inverted);
    font-size: var(--font-size-xsmall);
    font-weight: var(--font-weight-bold);
    padding: var(--space-xtiny) var(--space-xxxsmall);
    border-radius: var(--border-radius-tiny);
    z-index: 9999;
  }

  &__loader {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 100vh;
    min-height: 100dvh;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
